import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, uploadUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Camera, Check, Loader2, Lock, UploadCloud, Users, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { PhotoUploadCrop } from "@/components/PhotoUploadCrop";
import ClubCombobox from "@/components/ClubCombobox";

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0"));

const isFullDayMonth = (v) => /^\d{2}-(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)$/.test(String(v || ""));

function parseDayMonth(value) {
    if (!value) return { day: "", month: "" };
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (iso) return { day: iso[3], month: MONTHS_SHORT[parseInt(iso[2], 10) - 1] || "" };
    const m = /^(\d{1,2})-([A-Z][a-z]{2})$/.exec(value);
    if (m) return { day: m[1].padStart(2, "0"), month: m[2] };
    return { day: "", month: "" };
}

const initialForm = {
    email: "",
    birthday: "",
    postal_address: "",
    classification: "",
    // Iter 82: only used when the current DB club_name is blank — the
    // public form renders an editable input in that case (see `hasClub`).
    club_name: "",
    spouse_name: "",
    spouse_birthday: "",
    anniversary: "",
    // Iter 100j · optional spouse contact fields. Excluded from the
    // profile-completion score — safe to leave blank.
    spouse_phone: "",
    spouse_email: "",
};

export default function MemberForm() {
    const { token } = useParams();
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState("");
    const [spousePhotoFile, setSpousePhotoFile] = useState(null);
    const [spousePhotoPreview, setSpousePhotoPreview] = useState("");
    const [removeSpousePhoto, setRemoveSpousePhoto] = useState(false);
    const [errors, setErrors] = useState({});
    const [notFound, setNotFound] = useState(false);
    const [consent, setConsent] = useState(false);
    const photoRef = useRef(null);
    const spousePhotoRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/form/${token}`);
                setPayload(data);
                setForm({
                    email: data.editable.email || "",
                    birthday: data.editable.birthday || "",
                    postal_address: data.editable.postal_address || "",
                    classification: data.editable.classification || "",
                    club_name: "",  // populated by member only when locked is blank
                    spouse_name: data.editable.spouse_name || "",
                    spouse_birthday: data.editable.spouse_birthday || "",
                    anniversary: data.editable.anniversary || "",
                    spouse_phone: data.editable.spouse_phone || "",
                    spouse_email: data.editable.spouse_email || "",
                });
                // Iter 95: If consent has already been captured server-side,
                // pre-tick so submit stays enabled on subsequent saves.
                setConsent(!!data?.meta?.consent_accepted_at);
            } catch (err) {
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    const hasSpouse = form.spouse_name.trim().length > 0;

    // Optimistic client-side progress (server progress is authoritative on save)
    const progress = useMemo(() => {
        if (!payload) return 0;
        const filled = [];
        const total = [];
        const check = (key, val) => { total.push(key); if (String(val || "").trim()) filled.push(key); };
        // Locked contributes if pre-filled
        check("name", payload.locked.name);
        check("phone", payload.locked.phone);
        // Editable core
        check("email", form.email);
        check("birthday", isFullDayMonth(form.birthday) ? form.birthday : "");
        check("address", form.postal_address);
        // Photo
        total.push("photo");
        if (photoFile || payload.editable.photo_filename) filled.push("photo");
        // Committee (locked, already assigned by admin)
        total.push("committee");
        if ((payload.locked.committees || []).length > 0) filled.push("committee");
        // Spouse conditional
        if (hasSpouse) {
            check("spouse_birthday", isFullDayMonth(form.spouse_birthday) ? form.spouse_birthday : "");
            check("anniversary", isFullDayMonth(form.anniversary) ? form.anniversary : "");
            total.push("spouse_photo");
            if ((spousePhotoFile || (payload.editable.spouse_photo_filename && !removeSpousePhoto))) {
                filled.push("spouse_photo");
            }
        }
        return Math.round((filled.length / Math.max(total.length, 1)) * 100);
        // `hasSpouse` is derived from `form.spouse_name`, so `form` already covers it.
    }, [form, photoFile, spousePhotoFile, removeSpousePhoto, payload]);

    const handlePhoto = (e, isSpouse) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) {
            toast.error("Photo must be a JPG or PNG");
            e.target.value = "";
            return;
        }
        const preview = URL.createObjectURL(file);
        if (isSpouse) {
            setSpousePhotoFile(file);
            setSpousePhotoPreview(preview);
            setRemoveSpousePhoto(false);
        } else {
            setPhotoFile(file);
            setPhotoPreview(preview);
        }
    };

    // Called by PhotoUploadCrop with the already-cropped 272×272 JPEG File.
    // File-type + size checks are enforced inside PhotoUploadCrop; here we
    // just wire it up like handlePhoto so the rest of the save path stays
    // untouched.
    const acceptCroppedPhoto = (file, isSpouse) => {
        const preview = URL.createObjectURL(file);
        if (isSpouse) {
            setSpousePhotoFile(file);
            setSpousePhotoPreview(preview);
            setRemoveSpousePhoto(false);
        } else {
            setPhotoFile(file);
            setPhotoPreview(preview);
        }
    };

    const validate = () => {
        if (!payload) return false;
        const e = {};
        if (!String(form.email || "").trim()) e.email = "Required";
        if (!isFullDayMonth(form.birthday)) e.birthday = "Pick day and month";
        if (!String(form.postal_address || "").trim()) e.postal_address = "Required";
        if (!photoFile && !payload.editable.photo_filename) e.photo = "Photo is required";
        if (hasSpouse) {
            if (!isFullDayMonth(form.spouse_birthday)) e.spouse_birthday = "Pick day and month";
            if (!isFullDayMonth(form.anniversary)) e.anniversary = "Pick day and month";
            const spouseHasPhoto = !!(spousePhotoFile || (payload.editable.spouse_photo_filename && !removeSpousePhoto));
            if (!spouseHasPhoto) e.spouse_photo = "Spouse photo required";
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const buildFd = (submit) => {
        const fd = new FormData();
        fd.append("email", form.email || "");
        fd.append("birthday", form.birthday || "");
        fd.append("postal_address", form.postal_address || "");
        fd.append("classification", (form.classification || "").slice(0, 70));
        // Iter 82: only include club_name when the locked value is blank —
        // avoids ever submitting a member-edited club when the admin has
        // already set one. Backend also guards this (never overwrites a
        // populated club_name from the public form).
        if (!payload?.locked?.club_name && (form.club_name || "").trim()) {
            fd.append("club_name", form.club_name.trim());
        }
        fd.append("spouse_name", form.spouse_name || "");
        fd.append("spouse_birthday", hasSpouse ? (form.spouse_birthday || "") : "");
        fd.append("anniversary", hasSpouse ? (form.anniversary || "") : "");
        // Iter 100j · optional spouse contact fields. Sent only when a
        // spouse name is present; cleared otherwise so the form can
        // remove them cleanly. Not part of profile completion.
        fd.append("spouse_phone", hasSpouse ? (form.spouse_phone || "") : "");
        fd.append("spouse_email", hasSpouse ? (form.spouse_email || "") : "");
        if (photoFile) fd.append("photo", photoFile);
        if (spousePhotoFile) fd.append("spouse_photo", spousePhotoFile);
        fd.append("remove_spouse_photo", removeSpousePhoto ? "true" : "false");
        fd.append("submit", submit ? "true" : "false");
        // Iter 95 — mandatory data-processing consent when submitting.
        fd.append("consent", consent ? "true" : "false");
        return fd;
    };

    const save = async (submit) => {
        if (submit && !validate()) {
            toast.error("Please complete the required fields");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post(`/form/${token}`, buildFd(submit));
            setPayload(data);
            setForm({
                email: data.editable.email || "",
                birthday: data.editable.birthday || "",
                postal_address: data.editable.postal_address || "",
                classification: data.editable.classification || "",
                spouse_name: data.editable.spouse_name || "",
                spouse_birthday: data.editable.spouse_birthday || "",
                anniversary: data.editable.anniversary || "",
                spouse_phone: data.editable.spouse_phone || "",
                spouse_email: data.editable.spouse_email || "",
            });
            setPhotoFile(null); setSpousePhotoFile(null);
            setPhotoPreview(""); setSpousePhotoPreview("");
            setRemoveSpousePhoto(false);
            toast.success(submit ? "Profile submitted. Thank you!" : "Progress saved");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail?.missing_fields) {
                toast.error(`Missing: ${detail.missing_fields.join(", ")}`);
            } else {
                toast.error(typeof detail === "string" ? detail : "Failed to save");
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen grid place-items-center text-muted-foreground" data-testid="form-loading">
                Loading your profile…
            </div>
        );
    }
    if (notFound) {
        return (
            <div className="min-h-screen grid place-items-center noise-bg" data-testid="form-invalid">
                <div className="max-w-md rounded-2xl border bg-white p-8 text-center">
                    <h1 className="rotary-heading text-3xl text-rotary-ink">Link not found</h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                        This profile link is invalid or has expired. Please contact your district administrator.
                    </p>
                </div>
            </div>
        );
    }

    const { locked, meta } = payload;
    const submitted = meta.submitted;
    // Flatten per-assignment `displays` (already composed as
    // "Committee: Sub - Position") into a single list of chip strings.
    // Falls back to the older shape for a graceful upgrade window.
    const committeeChips = (locked.committees || []).flatMap((c) => {
        if (Array.isArray(c.displays) && c.displays.length) return c.displays;
        const parts = [];
        const label = (c.committee_label || "").trim();
        const positions = (c.positions || []).filter(Boolean);
        if (label && positions.length) {
            positions.forEach((p) => parts.push(`${label} - ${p}`));
        } else if (label) {
            parts.push(label);
        } else if (positions.length) {
            positions.forEach((p) => parts.push(p));
        }
        return parts;
    });

    return (
        <div className="min-h-screen bg-background pb-24" data-testid="member-form-page">
            {/* Sticky header with progress */}
            <header className="sticky top-0 z-30 glass-panel">
                <div className="mx-auto max-w-2xl px-5 py-4">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                            {meta.district_crest_url ? (
                                <img
                                    src={meta.district_crest_url}
                                    alt=""
                                    className="h-10 w-10 rounded-lg object-cover border shrink-0"
                                    data-testid="form-district-crest"
                                />
                            ) : (
                                <div className="grid h-10 w-10 place-items-center rounded-lg bg-rotary-blue text-white shrink-0">
                                    <Users className="h-4 w-4" />
                                </div>
                            )}
                            <div className="min-w-0">
                                <p className="label-eyebrow leading-none truncate">{meta.district_name || "Directory"}</p>
                                <p className="rotary-heading text-base leading-tight text-rotary-ink truncate">
                                    Year {meta.year_label}
                                </p>
                            </div>
                        </div>
                        <span
                            className={`rotary-chip whitespace-nowrap ${
                                submitted
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                    : "bg-rotary-gold/10 text-rotary-ink border-rotary-gold/40"
                            }`}
                            data-testid="form-status-chip"
                        >
                            {submitted ? "Submitted" : "Draft"}
                        </span>
                    </div>
                    <div className="mt-3 space-y-1.5">
                        <div className="flex items-center justify-between text-[11px]">
                            <span className="label-eyebrow">Profile complete</span>
                            <span className="font-semibold text-rotary-ink tabular-nums" data-testid="form-progress-value">
                                {progress}%
                            </span>
                        </div>
                        <Progress value={progress} className="h-2 bg-muted [&>div]:rotary-progress-fill" data-testid="form-progress-bar" />
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-2xl px-5 py-8">
                <div className="rounded-2xl border bg-white p-6 sm:p-8 space-y-8 animate-fade-up">
                    <section className="space-y-1.5">
                        <span className="label-eyebrow">Welcome, {locked.name}</span>
                        <h1 className="rotary-heading text-3xl text-rotary-ink sm:text-4xl">
                            Update your details
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            Fields marked <span className="text-rotary-cranberry">*</span> are required. Locked fields <Lock className="inline h-3.5 w-3.5 mx-0.5 align-text-bottom" /> are managed by your district admin.
                        </p>
                        {meta.governor_name && (
                            <p className="text-xs text-muted-foreground pt-1">
                                District Governor · <span className="text-rotary-ink font-medium">{meta.governor_name}</span>
                            </p>
                        )}
                    </section>

                    {/* Visiting-card QR — only after profile is 100% and share_token is issued */}
                    {submitted && progress === 100 && meta.share_token && (
                        <section
                            className="rounded-2xl border-2 border-rotary-gold/40 bg-gradient-to-br from-rotary-gold/5 via-white to-rotary-blue/5 p-5 sm:p-6"
                            data-testid="visiting-card-panel"
                        >
                            <div className="flex items-start gap-4">
                                <div className="rounded-xl bg-white p-2 border shadow-sm shrink-0">
                                    <QRCodeSVG
                                        value={`${window.location.origin}/card/${meta.share_token}`}
                                        size={112}
                                        level="H"
                                        includeMargin={false}
                                        data-testid="visiting-card-qr"
                                    />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <span className="label-eyebrow text-rotary-gold">Your visiting card is ready</span>
                                    <h2 className="rotary-heading text-lg text-rotary-ink mt-1">
                                        <QrCode className="inline h-4 w-4 mr-1 -mt-0.5" />
                                        Share it in seconds
                                    </h2>
                                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                                        Anyone who scans this QR opens a public card with your contact info and can save you to their phone. Your profile edit-link stays private.
                                    </p>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        <a
                                            href={`/card/${meta.share_token}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center rounded-full bg-rotary-blue text-white px-3.5 py-1.5 text-xs font-medium hover:bg-rotary-sky"
                                            data-testid="visiting-card-open-btn"
                                        >
                                            Preview my card →
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Locked identity */}
                    <section className="rounded-xl border bg-muted/30 p-5 space-y-3">
                        <div className="flex items-center gap-2">
                            <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="label-eyebrow">Managed by district admin</span>
                        </div>
                        {(locked.role_display || locked.club_name) && (
                            <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="locked-role-row">
                                {locked.role_display && (
                                    <span
                                        className="rotary-chip bg-rotary-blue/5 border-rotary-blue/20 text-rotary-blue text-xs"
                                        data-testid="locked-role-display"
                                    >
                                        {locked.role_display}
                                    </span>
                                )}
                                {locked.club_name ? (
                                    <span className="rotary-chip text-xs" data-testid="locked-club-name">
                                        {locked.club_name}
                                    </span>
                                ) : (
                                    <div className="w-full sm:w-72" data-testid="editable-club-name-wrap">
                                        <Label className="label-eyebrow text-[10px]">Club name</Label>
                                        <div className="mt-1">
                                            <ClubCombobox
                                                value={form.club_name}
                                                onChange={(name) => setForm({ ...form, club_name: name })}
                                                placeholder="e.g. Rotary Club of Bangalore Midtown"
                                                testId="editable-club-name-input"
                                                className="h-8 text-xs"
                                            />
                                        </div>
                                        <p className="mt-1 text-[10px] text-muted-foreground">
                                            Start typing — pick your club from the official Rotary list. Your record locks once saved.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="grid gap-3 sm:grid-cols-2">
                            <LockedField label="Name" value={locked.name} testid="locked-name" />
                            <div>
                                <Label className="label-eyebrow flex items-center gap-1"><Lock className="h-3 w-3" /> Phone</Label>
                                <div className="mt-1">
                                    <Input
                                        value={locked.phone}
                                        disabled
                                        readOnly
                                        className="bg-muted/60"
                                        data-testid="locked-phone"
                                    />
                                </div>
                                <p className="mt-1 text-[11px] text-muted-foreground">
                                    Update the country code if your number is registered in a different country.
                                </p>
                            </div>
                            <div className="sm:col-span-2">
                                <Label className="label-eyebrow">Committee &amp; position</Label>
                                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="locked-committees">
                                    {committeeChips.length === 0 ? (
                                        <span className="text-xs italic text-muted-foreground">No committee or position assigned yet</span>
                                    ) : committeeChips.map((label, idx) => (
                                        <span
                                            key={`${label}-${idx}`}
                                            className="rotary-chip text-[11px] bg-rotary-blue/10 text-rotary-blue border-rotary-blue/20"
                                            data-testid={`locked-committee-chip-${idx}`}
                                        >
                                            {label}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                            To change any of the above, please contact your district administrator.
                        </p>
                    </section>

                    {/* Photo — Upload + crop (1:1). Enforced 272×272 min. */}
                    <div className="space-y-1.5">
                        <div className="flex items-center gap-1.5">
                            <span className="label-eyebrow">Your photo</span>
                            <span className="text-rotary-cranberry">*</span>
                        </div>
                        <PhotoUploadCrop
                            subject="member"
                            previewSrc={photoPreview || (payload.editable.photo_filename ? uploadUrl(payload.editable.photo_filename, payload.editable.updated_at, token) : "")}
                            onCropped={(file) => acceptCroppedPhoto(file, false)}
                            testid="photo"
                            required
                        />
                        {errors.photo && <p className="text-xs text-rotary-cranberry">{errors.photo}</p>}
                    </div>

                    {/* Editable fields */}
                    <section className="grid gap-4 sm:grid-cols-2">
                        <FormField label="Email" required error={errors.email}>
                            <Input
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                data-testid="field-email"
                            />
                        </FormField>
                        <FormField label="Birthday" required error={errors.birthday}>
                            <DayMonthPicker
                                value={form.birthday}
                                onChange={(v) => setForm({ ...form, birthday: v })}
                                testid="field-birthday"
                            />
                        </FormField>
                        <div className="sm:col-span-2">
                            <FormField label="Communication address" required error={errors.postal_address}>
                                <Textarea
                                    rows={3}
                                    value={form.postal_address}
                                    onChange={(e) => setForm({ ...form, postal_address: e.target.value })}
                                    data-testid="field-postal-address"
                                />
                            </FormField>
                        </div>
                        <div className="sm:col-span-2">
                            <FormField label="Classification (profession, up to 70 characters)">
                                <div className="relative">
                                    <Input
                                        value={form.classification || ""}
                                        onChange={(e) => setForm({ ...form, classification: e.target.value.slice(0, 70) })}
                                        maxLength={70}
                                        placeholder="e.g. Chartered Accountant"
                                        data-testid="field-classification"
                                    />
                                    <span
                                        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] tabular-nums text-muted-foreground"
                                        data-testid="field-classification-counter"
                                    >
                                        {(form.classification || "").length}/70
                                    </span>
                                </div>
                            </FormField>
                        </div>
                    </section>

                    {/* Spouse */}
                    <section className="rounded-xl border bg-muted/40 p-5 space-y-4">
                        <div>
                            <span className="label-eyebrow">Spouse (optional)</span>
                            <p className="text-xs text-muted-foreground mt-1">
                                Adding a spouse name makes birthday, anniversary and photo required.
                            </p>
                        </div>
                        <FormField label="Spouse name">
                            <Input
                                value={form.spouse_name}
                                onChange={(e) => setForm({ ...form, spouse_name: e.target.value })}
                                data-testid="field-spouse-name"
                            />
                        </FormField>
                        {hasSpouse && (
                            <div className="grid gap-4 sm:grid-cols-2 animate-fade-up">
                                <FormField label="Spouse birthday" required error={errors.spouse_birthday}>
                                    <DayMonthPicker
                                        value={form.spouse_birthday}
                                        onChange={(v) => setForm({ ...form, spouse_birthday: v })}
                                        testid="field-spouse-birthday"
                                    />
                                </FormField>
                                <FormField label="Anniversary" required error={errors.anniversary}>
                                    <DayMonthPicker
                                        value={form.anniversary}
                                        onChange={(v) => setForm({ ...form, anniversary: v })}
                                        testid="field-anniversary"
                                    />
                                </FormField>
                                {/* Iter 100j · optional spouse contact fields.
                                    Not required, not counted in completion. */}
                                <FormField label="Spouse phone (optional)">
                                    <Input
                                        value={form.spouse_phone}
                                        onChange={(e) => setForm({ ...form, spouse_phone: e.target.value })}
                                        inputMode="tel"
                                        maxLength={20}
                                        placeholder="e.g. 9876543210"
                                        data-testid="field-spouse-phone"
                                    />
                                </FormField>
                                <FormField label="Spouse email (optional)">
                                    <Input
                                        type="email"
                                        value={form.spouse_email}
                                        onChange={(e) => setForm({ ...form, spouse_email: e.target.value })}
                                        placeholder="spouse@example.com"
                                        data-testid="field-spouse-email"
                                    />
                                </FormField>
                                <div className="sm:col-span-2">
                                    <div className="space-y-1.5">
                                        <div className="flex items-center gap-1.5">
                                            <span className="label-eyebrow">Spouse photo</span>
                                            <span className="text-rotary-cranberry">*</span>
                                        </div>
                                        <PhotoUploadCrop
                                            subject="spouse"
                                            previewSrc={
                                                spousePhotoPreview ||
                                                (!removeSpousePhoto && payload.editable.spouse_photo_filename
                                                    ? uploadUrl(payload.editable.spouse_photo_filename, payload.editable.updated_at, token)
                                                    : "")
                                            }
                                            onCropped={(file) => acceptCroppedPhoto(file, true)}
                                            onRemove={
                                                (spousePhotoPreview || (payload.editable.spouse_photo_filename && !removeSpousePhoto))
                                                    ? () => { setSpousePhotoFile(null); setSpousePhotoPreview(""); setRemoveSpousePhoto(true); }
                                                    : undefined
                                            }
                                            testid="spouse-photo"
                                        />
                                        {errors.spouse_photo && <p className="text-xs text-rotary-cranberry">{errors.spouse_photo}</p>}
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>

                    {/* Iter 95 — Consent (mandatory to submit) */}
                    <section
                        className="rounded-xl border border-rotary-gold/40 bg-rotary-cream/60 p-4"
                        data-testid="consent-section"
                    >
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={consent}
                                onChange={(e) => setConsent(e.target.checked)}
                                className="mt-1 h-4 w-4 rounded border-rotary-blue accent-rotary-blue"
                                data-testid="consent-checkbox"
                                aria-required="true"
                            />
                            <span className="text-xs leading-relaxed text-rotary-ink">
                                By completing this profile, I authorize Commworks—as an official partner authorized by
                                {" "}
                                <strong>RI District {payload?.meta?.district_number || payload?.locked?.ri_district || ""}</strong>
                                {" "}
                                for the collection and compilation of member data—to store and display my personal and
                                professional information within the official member directory. I confirm that the information
                                provided is accurate and I have the right to revoke this consent at any time.
                            </span>
                        </label>
                    </section>

                    {/* Actions */}
                    <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => save(false)}
                            disabled={saving}
                            className="rounded-full"
                            data-testid="save-draft-btn"
                        >
                            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                            Save draft
                        </Button>
                        <Button
                            type="button"
                            onClick={() => save(true)}
                            disabled={saving || !consent}
                            className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky"
                            data-testid="submit-form-btn"
                            title={consent ? "" : "Please tick the consent checkbox to submit"}
                        >
                            {saving ? (
                                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                            ) : (
                                <Check className="mr-1.5 h-4 w-4" />
                            )}
                            Submit profile
                        </Button>
                    </div>
                </div>
            </main>
        </div>
    );
}

function LockedField({ label, value, testid }) {
    return (
        <div>
            <Label className="label-eyebrow flex items-center gap-1.5">
                {label}
                <Lock className="h-3 w-3 text-muted-foreground" />
            </Label>
            <p
                className="mt-2 rounded-md bg-white px-3 py-2 text-sm text-rotary-ink border border-dashed"
                data-testid={testid}
            >
                {value || <span className="text-muted-foreground italic">Not set</span>}
            </p>
        </div>
    );
}

function FormField({ label, required, error, children }) {
    return (
        <div>
            <Label className="label-eyebrow">
                {label}
                {required && <span className="text-rotary-cranberry"> *</span>}
            </Label>
            <div className="mt-2">{children}</div>
            {error && (
                <p className="mt-1 text-xs text-rotary-cranberry" data-testid="field-error">
                    {error}
                </p>
            )}
        </div>
    );
}

function PhotoField({ label, required, preview, onChange, inputRef, onRemove, canRemove, testid, error }) {
    const cameraInputRef = useRef(null);
    return (
        <div>
            <Label className="label-eyebrow">
                {label}
                {required && <span className="text-rotary-cranberry"> *</span>}
            </Label>
            <div className="mt-2 flex flex-wrap items-center gap-4">
                <div
                    className="h-28 w-28 shrink-0 rounded-2xl border bg-muted/40 overflow-hidden grid place-items-center"
                    data-testid={`${testid}-preview`}
                >
                    {preview ? (
                        <img src={preview} alt="preview" className="h-full w-full object-cover" />
                    ) : (
                        <Camera className="h-8 w-8 text-muted-foreground" />
                    )}
                </div>
                <div className="flex flex-col gap-2">
                    {/* Gallery / file picker — no capture attr so mobile shows Files/Photos */}
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={onChange}
                        className="hidden"
                        data-testid={`${testid}-input`}
                    />
                    {/* Dedicated camera capture — separate input so we can force capture only here */}
                    <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        capture="environment"
                        onChange={onChange}
                        className="hidden"
                        data-testid={`${testid}-camera-input`}
                    />
                    <div className="flex flex-wrap gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => inputRef.current?.click()}
                            className="rounded-full border-rotary-blue text-rotary-blue hover:bg-rotary-blue hover:text-white"
                            data-testid={`${testid}-choose-btn`}
                        >
                            <UploadCloud className="mr-1.5 h-4 w-4" /> {preview ? "Replace" : "From gallery"}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => cameraInputRef.current?.click()}
                            className="rounded-full border-rotary-blue text-rotary-blue hover:bg-rotary-blue hover:text-white"
                            data-testid={`${testid}-camera-btn`}
                        >
                            <Camera className="mr-1.5 h-4 w-4" /> Camera
                        </Button>
                        {onRemove && canRemove && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onRemove}
                                className="rounded-full text-rotary-cranberry border-rotary-cranberry/40 hover:bg-rotary-cranberry hover:text-white"
                                data-testid={`${testid}-remove-btn`}
                            >
                                Remove
                            </Button>
                        )}
                    </div>
                    <p className="text-xs text-muted-foreground">JPG or PNG. Camera or gallery.</p>
                </div>
            </div>
            {error && (
                <p className="mt-1 text-xs text-rotary-cranberry" data-testid={`${testid}-error`}>
                    {error}
                </p>
            )}
        </div>
    );
}

function DayMonthPicker({ value, onChange, testid }) {
    const parsed = parseDayMonth(value);
    const [day, setDay] = useState(parsed.day);
    const [month, setMonth] = useState(parsed.month);
    const lastEmitted = useRef(value || "");

    useEffect(() => {
        if ((value || "") !== lastEmitted.current) {
            const p = parseDayMonth(value);
            setDay(p.day);
            setMonth(p.month);
            lastEmitted.current = value || "";
        }
    }, [value]);

    const emit = (d, m) => {
        const next = d && m ? `${d}-${m}` : "";
        lastEmitted.current = next;
        onChange(next);
    };

    return (
        <div className="grid grid-cols-2 gap-2">
            <Select value={day || undefined} onValueChange={(d) => { setDay(d); emit(d, month); }}>
                <SelectTrigger data-testid={`${testid}-day`}>
                    <SelectValue placeholder="Day" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                    {DAYS.map((d) => (
                        <SelectItem key={d} value={d} data-testid={`${testid}-day-opt-${d}`}>{d}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <Select value={month || undefined} onValueChange={(m) => { setMonth(m); emit(day, m); }}>
                <SelectTrigger data-testid={`${testid}-month`}>
                    <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                    {MONTHS_SHORT.map((m) => (
                        <SelectItem key={m} value={m} data-testid={`${testid}-month-opt-${m}`}>{m}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}
